import { normalizeSsoGateState, SsoGateState } from './officeSsoGate'

/**
 * Persistence for the Office SSO gate. Read and written from inside promise callbacks rather than
 * during render, and it has to be correct synchronously — the next `getAccessToken` decision reads
 * it immediately — so this goes straight at `Storage` instead of through `useLocalStorage`. Same
 * reasoning as `countRefreshFailure` in `loginTelemetry.ts`.
 *
 * The key is global, not per account, and that is deliberate: Office throttles per Outlook
 * process, not per mailbox, so after an account switch the same Outlook is still throttled.
 * `clearLocalStorageGlobals` in AuthorizationContext only wipes `global-auth*`, so this survives a
 * reset — as it must, exactly like `global-loginFailures`.
 */
const SSO_GATE_KEY = 'global-officeSsoGate'

/** The slice of the Storage API this needs; injectable so it can be exercised without a DOM. */
export type GateStorage = Pick<Storage, 'getItem' | 'setItem'>

/**
 * The stored gate state, or `undefined` when there is nothing trustworthy to act on.
 *
 * **Fails open on everything**: an absent key, unparseable JSON, a record older than the TTL, a
 * record from the future, or a storage that throws (privacy mode, a webview that partitions it).
 * All of those mean "no state", which means the caller may call Office. A bug here can cost the
 * throttle protection; it can never cost a login.
 */
export const readSsoGateState = (storage: GateStorage, now: number): SsoGateState | undefined => {
    try {
        const raw = storage.getItem(SSO_GATE_KEY)
        return raw ? normalizeSsoGateState(JSON.parse(raw), now) : undefined
    } catch (e) {
        console.error(e)
        return undefined
    }
}

/** Persist the gate state. A storage that refuses the write degrades this to an in-memory gate. */
export const writeSsoGateState = (storage: GateStorage, state: SsoGateState): void => {
    try {
        storage.setItem(SSO_GATE_KEY, JSON.stringify(state))
    } catch (e) {
        console.error(e)
    }
}
